import React, { useState } from 'react'
import Modal from '../Modal/Modal'
import InputField from '../input-field/InputField'
import SearchableSelect from '../input-field/SearchableSelect'
import DatePicker from 'react-datepicker'
import ArrowLeftWithBody from '../../assets/icons/ArrowLeftWithBody'
import CheckMark from '../../assets/icons/CheckMark'
import Delete from '../../assets/icons/Delete'
import { CustomDateInput } from '../CustomDateInput'
import { showSnackbar } from '../../misc/utils'

interface Teacher {
  id: number;
  firstname: string;
  lastname: string;
}

interface Subject {
  id: number;
  code: string;
  name: string;
}

interface Specialty {
  id: number;
  name: string;
}

interface LessonType {
  id: number;
  name: string;
}

interface CreateLessonModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  onSubmit?: (lessonData: LessonFormData) => void;
}

interface LessonFormData {
  teacher: Teacher | null;
  subjectCode: string;
  subjectName: string;
  specialties: Specialty[];
  lessonType: LessonType | null;
  lessonCount: number;
  date: Date | null;
  startTime: string;
  endTime: string;
}

// Моковые данные для демонстрации
const mockTeachers: Teacher[] = [
  { id: 1, firstname: 'Иван', lastname: 'Петров' },
  { id: 2, firstname: 'Мария', lastname: 'Сидорова' },
  { id: 3, firstname: 'Алексей', lastname: 'Козлов' },
];

const mockSubjects: Subject[] = [
  { id: 1, code: 'MATH101', name: 'Математический анализ' },
  { id: 2, code: 'PHYS201', name: 'Физика' },
  { id: 3, code: 'CS301', name: 'Программирование' },
];

const mockSpecialties: Specialty[] = [
  { id: 1, name: 'Информатика' },
  { id: 2, name: 'Математика' },
  { id: 3, name: 'Физика' },
  { id: 4, name: 'Экономика' },
];

const mockLessonTypes: LessonType[] = [
  { id: 1, name: 'Лекция' },
  { id: 2, name: 'Практика' },
  { id: 3, name: 'Лабораторная' },
  { id: 4, name: 'Семинар' },
];

const timeSlots = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30'
];

export default function CreateLessonModal({ 
  visible, 
  setVisible, 
  onSubmit 
}: CreateLessonModalProps) {
  const [formData, setFormData] = useState<LessonFormData>({
    teacher: null,
    subjectCode: '',
    subjectName: '',
    specialties: [],
    lessonType: null,
    lessonCount: 1,
    date: null,
    startTime: '',
    endTime: '',
  });

  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const transitionClasses = {
    modal: "transition-all duration-300 ease-in-out",
    inputFocus: "transition-all duration-200 ease-in-out",
    buttonState: "transition-all duration-200 ease-in-out",
    specialtyTag: "transition-all duration-200 ease-in-out"
  };

  const handleTeacherChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const teacherId = parseInt(e.target.value);
    const teacher = mockTeachers.find(t => t.id === teacherId);
    setFormData(prev => ({ ...prev, teacher: teacher || null }));
  };

  const handleSubjectCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    const subject = mockSubjects.find(s => s.code === code);
    setFormData(prev => ({ 
      ...prev, 
      subjectCode: code,
      subjectName: subject?.name || ''
    }));
  };

  const handleSubjectNameChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const name = e.target.value;
    const subject = mockSubjects.find(s => s.name === name);
    setFormData(prev => ({ 
      ...prev, 
      subjectName: name,
      subjectCode: subject?.code || ''
    }));
  };

  const handleSpecialtyAdd = () => {
    if (selectedSpecialtyId) {
      const specialty = mockSpecialties.find(s => s.id === parseInt(selectedSpecialtyId));
      if (specialty && !formData.specialties.find(s => s.id === specialty.id)) {
        setFormData(prev => ({
          ...prev,
          specialties: [...prev.specialties, specialty]
        }));
        setSelectedSpecialtyId('');
      }
    }
  };

  const handleSpecialtyRemove = (specialtyId: number) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s.id !== specialtyId)
    }));
  };

  const handleLessonTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const typeId = parseInt(e.target.value);
    const lessonType = mockLessonTypes.find(t => t.id === typeId);
    setFormData(prev => ({ ...prev, lessonType: lessonType || null }));
  };

  const handleLessonCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    const parsed = Math.max(1, Math.min(99, parseInt(raw || '1', 10)));
    setFormData(prev => ({ ...prev, lessonCount: parsed }));
  };

  const handleDateChange = (date: Date | null) => {
    setFormData(prev => ({ ...prev, date }));
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, startTime: e.target.value }));
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, endTime: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;
    
    setIsSubmitting(true);
    
    try {
      // Имитация API запроса
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (onSubmit) {
        onSubmit(formData);
      }
    
      showSnackbar('Урок успешно создан!', 'success');
 
      setFormData({
        teacher: null,
        subjectCode: '',
        subjectName: '',
        specialties: [],
        lessonType: null,
        lessonCount: 1,
        date: null,
        startTime: '',
        endTime: '',
      });
      setSelectedSpecialtyId('');
      
      setVisible(false);
    } catch (error) {
      showSnackbar('Ошибка при создании урока. Попробуйте еще раз.', 'error');
      console.error('Error creating lesson:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setVisible(false);
  };

  const isFormValid = formData.teacher && 
    formData.subjectCode && 
    formData.subjectName && 
    formData.specialties.length > 0 && 
    formData.lessonType && 
    formData.date && 
    formData.startTime && 
    formData.endTime;

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      additionalClassName={`w-full sm:w-4/5 md:w-3/5 lg:w-2/5 max-w-4xl h-auto max-h-[90vh] rounded-xl overflow-y-auto ${transitionClasses.modal}`}
    >
      <div className="flex flex-col p-4 sm:p-6 md:p-8">
        <div className="flex flex-col">
          <span className="text-2xl text-[#1A1A1A] mb-8">
            Создание урока
          </span>

          {/* Ф.И.О. преподавателя */}
          <div className="mb-6">
            <InputField
              type="select"
              id="teacher"
              label="Ф.И.О. преподавателя"
              value={formData.teacher?.id?.toString() || ''}
              handleChange={handleTeacherChange}
              options={mockTeachers.map(teacher => ({
                value: teacher.id,
                label: `${teacher.lastname} ${teacher.firstname}`
              }))}
              additionalClassName="admin-input"
            />
          </div>

          {/* Шифр дисциплины и Название дисциплины */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <InputField
                type="select"
                id="subjectCode"
                label="Шифр дисциплины"
                value={formData.subjectCode}
                handleChange={handleSubjectCodeChange}
                options={mockSubjects.map(subject => ({
                  value: subject.code,
                  label: subject.code
                }))}
                additionalClassName="admin-input"
              />
            </div>
            <div className="flex-1">
              <InputField
                type="select"
                id="subjectName"
                label="Название дисциплины"
                value={formData.subjectName}
                handleChange={handleSubjectNameChange}
                options={mockSubjects.map(subject => ({
                  value: subject.name,
                  label: subject.name
                }))}
                additionalClassName="admin-input"
              />
            </div>
          </div>

          {/* Специальности */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-[#5F6B88] mb-2">
              Специальность(и)
            </label>
            
            {/* Выбранные специальности */}
            {formData.specialties.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.specialties.map(specialty => (
                  <div
                    key={specialty.id}
                    className={`flex items-center bg-valley-main/15 text-black px-3 py-1 rounded-lg text-sm ${transitionClasses.specialtyTag} hover:bg-valley-main/25`}
                  >
                    <span className="mr-2">{specialty.name}</span>
                    <button
                      onClick={() => handleSpecialtyRemove(specialty.id)}
                      className={`hover:bg-valley-red rounded-full p-1 ${transitionClasses.buttonState}`}
                    >
                      <Delete additionalStyle="text-valley-gray hover:text-white w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Селект для добавления специальностей */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1">
                <InputField
                  type="select"
                  id="specialtySelect"
                  value={selectedSpecialtyId}
                  handleChange={(e) => setSelectedSpecialtyId(e.target.value)}
                  options={mockSpecialties
                    .filter(s => !formData.specialties.find(selected => selected.id === s.id))
                    .map(specialty => ({
                      value: specialty.id,
                      label: specialty.name
                    }))}
                  additionalClassName="admin-input"
                />
              </div>
              <button
                onClick={handleSpecialtyAdd}
                disabled={!selectedSpecialtyId}
                className={`px-4 py-2 bg-valley-main text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${transitionClasses.buttonState}`}
              >
                Добавить
              </button>
            </div>
          </div>

          {/* Тип занятий и Кол-во занятий */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <InputField
                type="select"
                id="lessonType"
                label="Тип занятий"
                value={formData.lessonType?.id?.toString() || ''}
                handleChange={handleLessonTypeChange}
                options={mockLessonTypes.map(type => ({
                  value: type.id,
                  label: type.name
                }))}
                additionalClassName="admin-input"
              />
            </div>
            <div className="flex-1">
              <InputField
                type="text"
                id="lessonCount"
                label="Кол-во занятий"
                value={formData.lessonCount.toString()}
                handleChange={handleLessonCountChange}
                placeholder="Введите количество"
                additionalClassName="admin-input"
              />
            </div>
          </div>

          {/* Дата, Начало, Окончание */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4 mb-8">
            <div className="flex-1">
              <div className="flex flex-col">
                <label className="text-[#5F6B88] font-medium text-sm mb-2">
                  Дата
                </label>
                <div className="h-12"> 
                  <DatePicker
                    className='text-black '
                    calendarIconClassName='!text-valley-gray'
                    selected={formData.date}
                    onChange={handleDateChange}
                    customInput={<CustomDateInput hasTime={false} />}
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
              </div>
            </div>
            <div className="flex-1">
              <InputField
                type="select"
                id="startTime"
                label="Начало"
                value={formData.startTime}
                handleChange={handleStartTimeChange}
                options={timeSlots.map(time => ({
                  value: time,
                  label: time
                }))}
                additionalClassName="admin-input"
              />
            </div>
            <div className="flex-1">
              <InputField
                type="select"
                id="endTime"
                label="Окончание"
                value={formData.endTime}
                handleChange={handleEndTimeChange}
                options={timeSlots.map(time => ({
                  value: time,
                  label: time
                }))}
                additionalClassName="admin-input"
              />
            </div>
          </div>

          {/* Кнопки управления */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              className={`rounded-lg flex-1 flex flex-row items-center justify-center bg-valley-gray border py-3 ${transitionClasses.buttonState}`}
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <ArrowLeftWithBody additionalStyle="text-white" />
              <span className="ml-2 font-bold text-[15px] text-white">
                Вернуться
              </span>
            </button>
            <button
              className={`rounded-lg flex-1 flex flex-row items-center justify-center bg-valley-main border py-3 ${transitionClasses.buttonState} ${
                !isFormValid || isSubmitting ? "opacity-30 cursor-not-allowed" : ""
              }`}
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              <CheckMark additionalStyle="text-white" />
              <span className="ml-2 font-bold text-[15px] text-white">
                {isSubmitting ? 'Создание...' : 'Создать урок'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}


